import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import {
  color,
  device,
  fontWeight,
  H3,
  Pill,
  radius,
  shadow,
  SmallText,
  space,
} from '@ticketswap/solar'

const PreviewCardContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${radius.lg};
  min-height: ${space[256]};
  box-shadow: ${shadow.strong};
  background-color: ${color.space};
  display: flex;
  flex-direction: column-reverse;
  align-items: center;

  @media ${device.tablet} {
    display: grid;
    grid-auto-columns: 1fr 50%;
  }

  [data-theme='dark'] & {
    background-color: ${color.nova};
  }
`

const BannerContent = styled.div`
  position: relative;
  z-index: 1;
  padding: ${space[24]} ${space[16]} ${space[16]};
  grid-column: 1;

  @media ${device.tablet} {
    padding: ${space[24]};
  }

  h3 {
    font-weight: ${fontWeight.bold};
    color: ${color.nova};
  }

  span {
    display: block;
    color: ${color.nova};
  }

  [data-theme='dark'] & {
    span,
    h3 {
      color: ${color.space};
    }
  }

  button:first-of-type {
    margin-top: ${space[16]};
    width: 100%;

    @media ${device.tablet} {
      width: auto;
    }
  }
`

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.image});
  width: 80%;

  ${props =>
    props.blurred &&
    css`
      filter: blur(15px);
      transform: scale(1.2);
    `}

  &::before {
    position: absolute;
    content: '';
    width: 100%;
    height: 100%;
    background-image: linear-gradient(
      to right,
      rgba(0, 19, 25, 1) 0%,
      rgba(0, 19, 25, 0.738) 19%,
      rgba(0, 19, 25, 0.541) 34%,
      rgba(0, 19, 25, 0.382) 47%,
      rgba(0, 19, 25, 0.278) 56.5%,
      rgba(0, 19, 25, 0.194) 65%,
      rgba(0, 19, 25, 0.126) 73%,
      rgba(0, 19, 25, 0.075) 80.2%,
      rgba(0, 19, 25, 0.042) 86.1%,
      rgba(0, 19, 25, 0.021) 91%,
      rgba(0, 19, 25, 0.008) 95.2%,
      rgba(0, 19, 25, 0.002) 98.2%,
      rgba(0, 19, 25, 0) 100%
    );
  }
`

const Content = styled.div`
  position: relative;
  grid-column: 2;
  align-self: center;
  width: 100%;
`

const StyledPill = styled(Pill)`
  position: absolute;
  top: ${space[8]};
  right: ${space[8]};
  z-index: 1;
`

export const PreviewCard = ({
  children,
  image,
  blurred,
  title,
  text,
  button,
  employeeOnly,
  ...props
}) => (
  <PreviewCardContainer {...props}>
    <BannerContent>
      <H3>{title}</H3>
      {text && <SmallText>{text}</SmallText>}
      {button}
    </BannerContent>
    <BackgroundImage image={image} blurred={blurred} />

    {children && <Content>{children}</Content>}
    {employeeOnly && <StyledPill variant="warning">Employee only</StyledPill>}
  </PreviewCardContainer>
)

PreviewCard.defaultProps = {
  blurred: false,
  employeeOnly: false,
}

PreviewCard.propTypes = {
  image: PropTypes.string.isRequired,
  blurred: PropTypes.bool,
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  button: PropTypes.node,
  employeeOnly: PropTypes.bool,
}
